import { cn } from '@/lib/utils';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { Button } from '../ui/button';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField } from '../ui/form';
import { Input } from '../ui/input';
import { toNumber } from '@/lib/accounting';

export interface FormInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'form' | 'label'> {
    form: UseFormReturn<any>;
    label?: string;
    description?: string;
    name: string;
    render?: (field: FieldValues) => React.ReactNode;
    defaultValue?: string;
    togglePassword?: boolean;
    isRequired?: boolean;
    wrapperClassName?: string
    /** Optional control rendered to the right of the input (e.g. a mode-switch button). */
    inputAction?: React.ReactNode;
}

const FormInput = React.forwardRef<HTMLInputElement, FormInputProps>(
    (
        { className, wrapperClassName, name, type = 'text', form, label, inputAction, render, defaultValue, togglePassword = false, isRequired = false, description, ...props },
        ref,
    ) => {
        const { formItemId, formDescriptionId, formMessageId } = useFormField();
        const [showPassword, setShowPassword] = useState(false);

        const inputType = type === 'password' && showPassword ? 'text' : type;
        return (
            <FormField
                control={form.control}
                name={name}
                defaultValue={defaultValue}
                render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                    <FormItem className={wrapperClassName}>
                        <FormLabel>
                            {label} {isRequired && <span className="text-red-500">*</span>}
                        </FormLabel>
                        {description &&<FormDescription className="text-xs text-muted-foreground">{description}</FormDescription>}
                        <FormControl>
                            {render ? (
                                render({ onChange, value, ...field })
                            ) : (
                                <div className={cn('relative', inputAction && 'flex items-center gap-2')}>
                                    <Input
                                        type={inputType}
                                        className={cn('w-full', inputAction && 'min-w-0 flex-1', className, error && 'border-destructive focus-visible:ring-destructive')}
                                        ref={ref}
                                        value={value}
                                        onChange={
                                            (e) => {
                                                onChange(type === 'number' ? toNumber(e.target.value) : e.target.value);
                                            }
                                        }
                                        id={formItemId}
                                        aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
                                        aria-invalid={!!error}
                                        {...props}
                                    />
                                    {type === 'password' && togglePassword && (
                                        <Button
                                            className="absolute top-1/2 right-0.5 h-8 w-8 -translate-y-1/2 p-1"
                                            variant="ghost"
                                            size="icon"
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                                        </Button>
                                    )}
                                    {inputAction}
                                </div>
                            )}
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        );
    },
);
FormInput.displayName = 'FormInput';

export { FormInput };
